import { useMemo } from "react"
import { useDispatch } from "react-redux"
import {
  actions,
  ApplyEditorChangesPayload,
  BootstrapProductFeedPayload,
  CreateLineItemForProductIdAndTemplateIdPayload,
  CreateLineItemForProductIdAndTemplateIdWithPlaceholderImagePayload,
  CreateLineItemForProductIdPayload,
  CreateLineItemForProductIdWithPlaceholderImagePayload,
  CreateLineItemFromTemplatePayload,
  CreateLineItemPayload,
  CreateLineItemWithImagePayload,
  CreateOrderPayload,
  CreateUploadPayload,
  DownloadProductTemplates,
  DownloadProductTemplateTags,
  DuplicateLineItemPayload,
  ImageAITransformationPayload,
  InitStorePayload,
  InsertPageSpreadToLineItemPayload,
  PlaceOrderPayload,
  RemoveIncompletePageSpreadsFromLineItemPayload,
  RemoveLineItemPayload,
  RemovePageSpreadFromLineItemPayload,
  SearchStorePayload,
  SetUpdateMissingImagesForLineItemDialogChoice,
  SwapImageRegionFromPageAToPageBPayload,
  UpdateFulfillementPayload,
  UpdateImageRegionImage,
  UpdateLineItemOrientationPayload,
  UpdateLineItemProduct,
  UpdateLineItemProductTemplate,
  UpdateLineItemProductTemplateVariant,
  UpdateMissingImagesForLineItem,
  UpdateStorePayload,
  UploadImagesAndCreateLineItemsForProductIdPayload,
  UploadImagesAndCreateLineItemsForProductIdAndTemplateIdPayload,
  UploadUriImagesPayload,
  CreateLineItemForProductIdWithImageUrlPayload,
  ReplaceImageRegionImageWithImageUrlPayload,
  FillLineItemImageRegionsWithImageUrlsPayload,
  UpdateDocumentPayload,
} from "~/redux/process"
import {
  AnalyticAction,
  AnalyticEventPayload,
} from "~/redux/saga/processAnalyticEvents"
import { UpdateOrderSummaryReasonType } from "~/redux/saga/processUpdateOrderSummary"

export const useProcessActions = () => {
  const dispatch = useDispatch()

  const processes = useMemo(
    () => ({
      initStores: (payload: InitStorePayload) => {
        dispatch(actions.initStores(payload))
      },
      searchStores: (payload: SearchStorePayload) => {
        dispatch(actions.searchStores(payload))
      },
      updateOrderSummary: ({
        reason,
        reasonType,
      }: {
        reason: string
        reasonType?: UpdateOrderSummaryReasonType
      }) => {
        dispatch(actions.updateOrderSummary({ reason, reasonType }))
      },
      placeOrder: (payload: PlaceOrderPayload) => {
        dispatch(actions.placeOrder(payload))
      },
      createOrder: (payload: CreateOrderPayload) => {
        dispatch(actions.createOrder(payload))
      },
      applyCouponCode: () => {
        dispatch(actions.applyCouponCode({}))
      },
      createUpload: (payload: CreateUploadPayload) => {
        dispatch(actions.createUpload(payload))
      },
      createLineItem: (payload: CreateLineItemPayload) => {
        dispatch(actions.createLineItem(payload))
      },
      createLineItemWithImage: (payload: CreateLineItemWithImagePayload) => {
        dispatch(actions.createLineItemWithImage(payload))
      },
      createLineItemFromTemplate: (
        payload: CreateLineItemFromTemplatePayload
      ) => {
        dispatch(actions.createLineItemFromTemplate(payload))
      },
      duplicateLineItem: (payload: DuplicateLineItemPayload) => {
        dispatch(actions.duplicateLineItem(payload))
      },
      updateLineItemProduct: (payload: UpdateLineItemProduct) => {
        dispatch(actions.updateLineItemProduct(payload))
      },
      updateLineItemProductTemplate: (
        payload: UpdateLineItemProductTemplate
      ) => {
        dispatch(actions.updateLineItemProductTemplate(payload))
      },
      updateLineItemProductTemplateVariant: (
        payload: UpdateLineItemProductTemplateVariant
      ) => {
        dispatch(actions.updateLineItemProductTemplateVariant(payload))
      },
      updateLineItemOrientation: (
        payload: UpdateLineItemOrientationPayload
      ) => {
        dispatch(actions.updateLineItemOrientation(payload))
      },
      removeLineItem: (payload: RemoveLineItemPayload) => {
        dispatch(actions.removeLineItem(payload))
      },
      updateImageRegionImage: (payload: UpdateImageRegionImage) => {
        dispatch(actions.updateImageRegionImage(payload))
      },
      updateDocument: (payload: UpdateDocumentPayload) => {
        dispatch(actions.updateDocument(payload))
      },
      updateMissingImagesForLineItem: (
        payload: UpdateMissingImagesForLineItem
      ) => {
        dispatch(actions.updateMissingImagesForLineItem(payload))
      },
      setUpdateMissingImagesDialogChoice: (
        payload: SetUpdateMissingImagesForLineItemDialogChoice
      ) => {
        dispatch(actions.setUpdateMissingImagesDialogChoice(payload))
      },
      downloadProductTemplateTags: (payload: DownloadProductTemplateTags) => {
        dispatch(actions.downloadProductTemplateTags(payload))
      },
      downloadProductTemplates: (payload: DownloadProductTemplates) => {
        dispatch(actions.downloadProductTemplates(payload))
      },
      updateStore: (payload: UpdateStorePayload) => {
        dispatch(actions.updateStore(payload))
      },
      applyEditorChanges: (payload: ApplyEditorChangesPayload) => {
        dispatch(actions.applyEditorChanges(payload))
      },
      uploadUriImages: (payload: UploadUriImagesPayload) => {
        dispatch(actions.uploadUriImages(payload))
      },
      fireAnalyticEvent: (payload: AnalyticEventPayload) => {
        dispatch(AnalyticAction(payload))
      },
      insertPageSpreadToLineItemAfter: (
        payload: InsertPageSpreadToLineItemPayload
      ) => {
        dispatch(actions.insertPageSpreadToLineItemAfter(payload))
      },
      insertPageSpreadToLineItemBefore: (
        payload: InsertPageSpreadToLineItemPayload
      ) => {
        dispatch(actions.insertPageSpreadToLineItemBefore(payload))
      },
      removePageSpreadFromLineItem: (
        payload: RemovePageSpreadFromLineItemPayload
      ) => {
        dispatch(actions.removePageSpreadFromLineItem(payload))
      },
      removeIncompletePageSpreadsFromLineItem: (
        payload: RemoveIncompletePageSpreadsFromLineItemPayload
      ) => {
        dispatch(actions.removeIncompletePageSpreadsFromLineItem(payload))
      },
      swapImageRegionFromPageAToPageB: (
        payload: SwapImageRegionFromPageAToPageBPayload
      ) => {
        dispatch(actions.swapImageRegionFromPageAToPageB(payload))
      },
      applyImageAITransformation: (payload: ImageAITransformationPayload) => {
        dispatch(actions.applyImageAITransformation(payload))
      },

      bootstrapProductFeed: (payload: BootstrapProductFeedPayload) => {
        dispatch(actions.bootstrapProductFeed(payload))
      },

      updateFulfillment: (payload: UpdateFulfillementPayload) => {
        dispatch(actions.updateFulfillment(payload))
      },

      uploadImagesAndCreateLineItemsForProductId: (
        payload: UploadImagesAndCreateLineItemsForProductIdPayload
      ) => {
        dispatch(actions.uploadImagesAndCreateLineItemsForProductId(payload))
      },

      createLineItemForProductIdWithPlaceholderImage: (
        payload: CreateLineItemForProductIdWithPlaceholderImagePayload
      ) => {
        dispatch(
          actions.createLineItemForProductIdWithPlaceholderImage(payload)
        )
      },

      uploadImagesAndCreateLineItemsForProductIdAndTemplateId: (
        payload: UploadImagesAndCreateLineItemsForProductIdAndTemplateIdPayload
      ) => {
        dispatch(
          actions.uploadImagesAndCreateLineItemsForProductIdAndTemplateId(
            payload
          )
        )
      },

      createLineItemForProductIdAndTemplateIdWithPlaceholderImage: (
        payload: CreateLineItemForProductIdAndTemplateIdWithPlaceholderImagePayload
      ) => {
        dispatch(
          actions.createLineItemForProductIdAndTemplateIdWithPlaceholderImage(
            payload
          )
        )
      },

      createLineItemForProductId: (
        payload: CreateLineItemForProductIdPayload
      ) => {
        dispatch(actions.createLineItemForProductId(payload))
      },

      createLineItemForProductIdAndTemplateId: (
        payload: CreateLineItemForProductIdAndTemplateIdPayload
      ) => {
        dispatch(actions.createLineItemForProductIdAndTemplateId(payload))
      },

      createLineItemForProductIdWithImageUrl: (
        payload: CreateLineItemForProductIdWithImageUrlPayload
      ) => {
        dispatch(actions.createLineItemForProductIdWithImageUrl(payload))
      },

      replaceImageRegionImageWithImageUrl: (
        payload: ReplaceImageRegionImageWithImageUrlPayload
      ) => {
        dispatch(actions.replaceImageRegionImageWithImageUrl(payload))
      },

      fillLineItemImageRegionsWithImageUrls: (
        payload: FillLineItemImageRegionsWithImageUrlsPayload
      ) => {
        dispatch(actions.fillLineItemImageRegionsWithImageUrls(payload))
      },

      startFetchingVNZCloudImages: () => {
        dispatch(actions.startFetchingVNZCloudImages())
      },

      logoutVNZ: () => {
        dispatch(actions.logoutVNZ())
      },
    }),
    [dispatch]
  )

  return processes
}
